import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

interface EditModeContextValue {
  isEditMode: boolean;
  toggleEditMode: () => void;
}

const EditModeContext = createContext<EditModeContextValue>({ isEditMode: false, toggleEditMode: () => {} });

function getEntitySegment(pathname: string): string | null {
  const seg = pathname.split('/').filter(Boolean)[0];
  if (seg && ['sample', 'test', 'assay', 'order'].includes(seg)) return seg;
  return null;
}

export function EditModeProvider({ children }: { children: React.ReactNode }) {
  const [isEditMode, setIsEditMode] = useState(false);
  const originRef = useRef<string | null>(null);
  const location = useLocation();

  useEffect(() => {
    if (!isEditMode) {
      originRef.current = null;
      return;
    }
    if (originRef.current === null) {
      originRef.current = getEntitySegment(location.pathname) ?? location.pathname;
      return;
    }
    const currentSeg = getEntitySegment(location.pathname);
    if (currentSeg && currentSeg !== originRef.current) {
      setIsEditMode(false);
    }
  }, [location.pathname, isEditMode]);

  return (
    <EditModeContext.Provider value={{ isEditMode, toggleEditMode: () => setIsEditMode(prev => !prev) }}>
      {children}
    </EditModeContext.Provider>
  );
}

export function useEditMode() {
  return useContext(EditModeContext);
}
